/**
 * 共用登录页外壳（LeapWorldTwin-1000，设计正本 998 方向 B + 「登录遇到问题」）。
 * 电脑上左右分栏：左栏讲「这是哪个应用」，右栏讲「该点哪个按钮」；手机上左栏收成顶部条。
 * 只管版式和公共状态，不认识会话和接口：读会话、拼授权地址、真正发登录请求都留在应用里，
 * 应用把「怎么提交」作为函数传进来，外壳负责空提交校验、提交中锁按钮、失败提示。
 *
 * 颜色、圆角全取主题变量，不用图片、渐变、阴影；页面里只有一个 h1（应用名），用响应式类换位置和字号。
 * 版本号读构建期注入的 VITE_APP_VERSION，纯文字，不用 VersionBadge（它登录前会请求 /api/health）。
 */
import { createContext, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Alert } from './Alert'
import { BrandGlyph } from './BrandGlyph'
import { Button } from './Button'
import { Field } from './Field'
import { useSubmitLock } from './form-guards'
import { Input } from './Input'
import { Skeleton } from './Skeleton'

type LoginMethod = 'center' | 'feishu' | 'leapauth' | 'local'
/** LoginMethods 把自己的锁状态报给外壳，外壳再交给开发身份区，两边同一个在途状态。 */
const ReportMethodsLocked = createContext<(locked: boolean) => void>(() => undefined)
type ExternalMethod = Exclude<LoginMethod, 'local'>
type LoginHelpItem = { question: string; answer: string }

/** 应用的 shared/branding.ts 里登录页用到的几项；整个对象原样传进来。 */
type LoginBranding = {
  /** 应用名：左栏的 h1。 */
  name: string
  /** 字标，1–2 个字。 */
  glyph: string
  /** 一句业务说明。 */
  description: string
  /** 「这里能做什么」，最多 3 条；不给或空数组时左栏不画分隔线和列表。 */
  loginHighlights?: readonly string[]
  /** 「登录遇到问题」整组替换；不给就按登录方式生成默认条目。 */
  loginHelp?: readonly LoginHelpItem[]
}

const METHOD_LABEL: Record<ExternalMethod, string> = {
  center: '统一入口登录',
  feishu: '使用飞书登录',
  leapauth: '使用 LeapAuth 登录'
}

const METHOD_PENDING_LABEL: Record<ExternalMethod, string> = {
  center: '正在前往统一入口…',
  feishu: '正在前往飞书…',
  leapauth: '正在前往 LeapAuth…'
}

/** 默认「登录遇到问题」：按应用开的登录方式生成，每条都是「对象 + 原因 + 下一步」。 */
function defaultLoginHelp(methods: readonly LoginMethod[]): LoginHelpItem[] {
  const items: LoginHelpItem[] = []
  if (methods.includes('center')) {
    items.push({
      question: '统一入口打不开',
      answer: `统一入口页面打不开或一直在转：多半是网络或统一入口暂时不可用。${
        methods.includes('feishu') ? '请先改用飞书登录；还不行，请联系本系统管理员。' : '请稍后再试；还不行，请联系本系统管理员。'
      }`
    })
  }
  if (methods.includes('local')) {
    items.push({ question: '忘记密码', answer: '本系统不能自助找回密码。请联系本系统管理员重置。' })
  }
  items.push({ question: '登录后提示没有权限', answer: '账号能登录，但还没开通本系统的角色。请联系本系统管理员开通。' })
  return items
}

function LoginHelp({ items }: { items: readonly LoginHelpItem[] }) {
  return (
    <section aria-label="登录遇到问题" className="border-t border-[var(--color-line)] pt-4">
      <h3 className="m-0 text-[13px] font-semibold text-[var(--color-muted)]">登录遇到问题</h3>
      <dl className="m-0 mt-2 flex flex-col gap-2.5">
        {items.map((item) => (
          <div key={item.question}>
            <dt className="text-[13px] font-medium text-[var(--color-ink)]">{item.question}</dt>
            <dd className="m-0 text-[13px] leading-5 text-[var(--color-muted)]">{item.answer}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/** 统一入口回来后的两种结果：已提交绑定申请，或没有匹配到账号。 */
function CenterResultNotice({ result, methods }: { result: 'pending' | 'unmatched'; methods: readonly LoginMethod[] }) {
  const feishu = methods.includes('feishu')
  if (result === 'pending') {
    return <Alert tone="info" title="已提交绑定申请">请等待管理员确认。{feishu ? '也可使用飞书登录。' : methods.includes('local') ? '也可使用账号密码登录。' : ''}</Alert>
  }
  return <Alert tone="warning" title="暂未识别为已有账号">{feishu ? '请使用飞书登录，或联系管理员确认绑定。' : '请联系管理员确认绑定，或使用原账号登录。'}</Alert>
}

type LoginLayoutProps = {
  /** 应用的 shared/branding.ts 整个对象；左栏和手机顶部条都来自它。 */
  branding: LoginBranding
  /** 默认读构建期注入的 VITE_APP_VERSION；没有就不显示。 */
  version?: string
  /** 右栏标题，默认「登录」；绑定旧账号时是「绑定旧账号」。 */
  title?: string
  /** 绑定旧账号：标题换成「绑定旧账号」，不显示「登录遇到问题」（用户已经走到绑定这一步）。 */
  binding?: boolean
  /** 统一入口或飞书回调失败带回来的原文（?auth_error=）。 */
  authError?: string | null
  /** 统一入口回来后的结果：已提交绑定申请 / 没有匹配到账号。 */
  centerResult?: 'pending' | 'unmatched' | null
  /** 其他提醒：放在标题下面，应用自己用 Alert 组装。 */
  notice?: ReactNode
  /** 确认登录状态中：右栏画占位块，不画任何按钮，免得先闪出本地表单再变成飞书按钮。 */
  loading?: boolean
  /** 登录方式区：通常是 LoginMethods；首登改密时放改密表单。 */
  children?: ReactNode
  /** 不给就用 branding.loginHelp，再不给按 methods 生成默认条目；false 不显示。 */
  help?: readonly LoginHelpItem[] | false
  /** 应用开了哪些登录方式：生成默认的「登录遇到问题」、统一入口回来后的提示文字。 */
  methods: readonly LoginMethod[]
  /** 开发登录：应用按原来的条件传入，外壳只给虚线框和「仅开发环境」标签。
   *  传函数时收到 { locked }：本地提交中、外部方式跳转中、自带表单在途时为真，开发身份按钮请跟着禁用；
   *  开发身份自己的请求在途时，应用把它的在途状态传给 LoginMethods 的 busy，反过来锁住其余入口。 */
  devSlot?: ReactNode | ((state: { locked: boolean }) => ReactNode)
}

export function LoginLayout({
  branding,
  version = import.meta.env.VITE_APP_VERSION,
  title,
  binding = false,
  authError,
  centerResult,
  notice,
  loading = false,
  children,
  help,
  methods,
  devSlot
}: LoginLayoutProps) {
  const [methodsLocked, setMethodsLocked] = useState(false)
  const glyphLength = [...branding.glyph].length
  const listed = (branding.loginHighlights ?? []).slice(0, 3)
  const helpItems = help === false || binding ? null : (help ?? branding.loginHelp ?? defaultLoginHelp(methods))
  return (
    <div className="grid min-h-[100dvh] grid-cols-1 grid-rows-[auto_1fr_auto] bg-[var(--color-panel)] md:grid-cols-[minmax(480px,5fr)_7fr] md:grid-rows-[1fr_auto]">
      <aside className="bg-[var(--color-primary-soft)] px-5 py-5 md:col-start-1 md:row-span-2 md:row-start-1 md:flex md:items-center md:p-16">
        <div className="flex w-full items-center gap-3 md:max-w-[440px] md:flex-col md:items-start md:gap-5">
          <BrandGlyph size={40} className={`md:hidden ${glyphLength > 1 ? 'text-[15px]!' : 'text-[18px]!'}`}>{branding.glyph}</BrandGlyph>
          <BrandGlyph size={56} className={`hidden md:flex ${glyphLength > 1 ? 'text-[21px]!' : 'text-[26px]!'}`}>{branding.glyph}</BrandGlyph>
          <div className="min-w-0 md:contents">
            <h1 className="m-0 text-[20px] font-bold leading-7 text-[var(--color-ink)] [overflow-wrap:anywhere] md:text-[36px] md:leading-[44px]">{branding.name}</h1>
            <p className="m-0 text-[13px] leading-5 text-[var(--color-muted)] md:text-[17px] md:leading-[26px]">{branding.description}</p>
          </div>
          {listed.length ? (
            <ol className="m-0 hidden w-full list-none flex-col gap-3 border-t border-[var(--color-line-strong)] p-0 pt-6 md:flex">
              {listed.map((item, index) => (
                <li key={item} className="flex gap-0 text-[15px] leading-[22px] text-[var(--color-ink)]">
                  <span className="w-7 shrink-0 font-[family-name:var(--font-mono)] text-xs text-[var(--color-muted)]">{String(index + 1).padStart(2, '0')}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">{item}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </aside>
      <main className="px-5 py-6 md:col-start-2 md:row-span-2 md:row-start-1 md:flex md:flex-col md:justify-center md:px-12 md:py-14">
        <div className="mx-auto flex w-full max-w-[380px] flex-col gap-[18px]">
          <h2 className="m-0 text-[20px] font-bold leading-7 text-[var(--color-ink)] md:text-2xl md:leading-8">{title ?? (binding ? '绑定旧账号' : '登录')}</h2>
          {authError ? <Alert tone="danger" title="登录没有完成">{authError}</Alert> : null}
          {centerResult ? <CenterResultNotice result={centerResult} methods={methods} /> : null}
          {notice}
          {loading ? (
            <div role="status" className="flex flex-col gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <span className="text-[13px] text-[var(--color-muted)]">正在确认登录状态…</span>
            </div>
          ) : (
            <>
              <ReportMethodsLocked.Provider value={setMethodsLocked}>{children}</ReportMethodsLocked.Provider>
              {devSlot ? (
                <div className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-dashed border-[var(--color-warning)] bg-[var(--color-warning-soft)] p-3 text-[var(--color-warning-ink)]">
                  <span className="self-start rounded-[var(--radius-badge)] border border-[var(--color-warning)] px-2 text-xs">仅开发环境</span>
                  {typeof devSlot === 'function' ? devSlot({ locked: methodsLocked }) : devSlot}
                </div>
              ) : null}
              {helpItems ? <LoginHelp items={helpItems} /> : null}
            </>
          )}
        </div>
      </main>
      {version ? (
        <footer className="px-5 pb-6 text-center font-[family-name:var(--font-mono)] text-xs text-[var(--color-faint)] md:col-start-1 md:row-start-2 md:px-16 md:pb-10 md:text-left">
          版本 {version}
        </footer>
      ) : null}
    </div>
  )
}

type LocalLogin = {
  /** 两个都填了才会调用（用户名已去掉首尾空格）；失败请抛出，外壳把原因放在按钮上方，输入保留。 */
  onSubmit: (values: { username: string; password: string }) => void | Promise<unknown>
  /** 绑定旧账号：密码栏叫「旧密码」，按钮「绑定并登录」，说明框和失败标题都按绑定写。 */
  binding?: boolean
  /** 绑定时给「返回其他登录方式」；不给就不画。 */
  onBack?: () => void
  /** 用户名栏的名字，默认「用户名」；占位字同名，可用 usernamePlaceholder 改。 */
  usernameLabel?: string
  usernamePlaceholder?: string
  usernameType?: 'text' | 'email'
  autoFocus?: boolean
  /** 两栏没填满时按钮置灰，不走空提交提示。 */
  disableUntilFilled?: boolean
}

type LoginMethodsProps = {
  /** 已按部署配置过滤、按顺序排好；第一种外部方式是主按钮。 */
  methods: readonly LoginMethod[]
  /** 外部方式只通知应用，由应用自己跳授权地址；被点的按钮显示转圈和「正在前往…」，其余按钮和输入框全部锁住。
   *  返回 Promise 时，结束后（成功或失败）解锁；同步跳转的，页面从后退缓存回来时解锁。 */
  onStart: (method: ExternalMethod) => void | Promise<unknown>
  /** 标准本地账号表单：用户名 + 密码，状态、空提交、提交中都在外壳里。 */
  local?: LocalLogin
  /** 应用自带的本地表单（如 crm、market 的 Form）；和 local 二选一，同样放在分隔线下。 */
  children?: ReactNode
  /** 应用自带表单的请求在途时传 true：外部按钮和整块输入一起锁住，结束（成功或失败）传 false 解锁。用 local 时外壳自己管，不用传。 */
  busy?: boolean
}

/** 标准本地账号表单。 */
function LocalLoginForm({ local, first, locked, onBusy }: { local: LocalLogin; first: boolean; locked: boolean; onBusy: (busy: boolean) => void }) {
  const { pending, run } = useSubmitLock()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [invalid, setInvalid] = useState({ username: false, password: false })
  const [failure, setFailure] = useState<unknown>(null)
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => { onBusy(pending) }, [onBusy, pending])
  // 空提交：焦点落到第一个标红的输入框
  useEffect(() => {
    if (invalid.username || invalid.password) formRef.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus()
  }, [invalid])
  const binding = Boolean(local.binding)
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const name = username.trim()
    if (!name || !password) {
      setInvalid({ username: !name, password: !password })
      setFailure('请输入用户名和密码')
      return
    }
    setInvalid({ username: false, password: false })
    setFailure(null)
    await run(async () => {
      try {
        await local.onSubmit({ username: name, password })
      } catch (err) {
        setFailure(err || new Error(''))
      }
    })
  }
  const heights = 'min-h-10! max-md:min-h-11!'
  return (
    <>
      {failure ? <LoginFailureNotice error={failure} binding={binding} /> : null}
      {binding ? <Alert tone="info" title="首次使用统一登录">你的统一登录身份还未绑定本系统账号。请输入原账号与旧密码完成绑定；绑定失败请联系系统管理员确认。</Alert> : null}
      <form ref={formRef} className="flex flex-col gap-3.5" onSubmit={submit}>
        <Field label={local.usernameLabel ?? '用户名'} error={invalid.username ? `请输入${local.usernameLabel ?? '用户名'}` : undefined}>
          <Input
            type={local.usernameType}
            value={username}
            onChange={(event) => { setUsername(event.target.value); setInvalid((prev) => ({ ...prev, username: false })) }}
            placeholder={local.usernamePlaceholder ?? local.usernameLabel ?? '用户名'}
            autoComplete={local.usernameType === 'email' ? 'email' : 'username'}
            autoFocus={local.autoFocus}
            error={invalid.username}
            aria-invalid={invalid.username || undefined}
            disabled={locked}
            className={heights}
          />
        </Field>
        <Field label={binding ? '旧密码' : '密码'} error={invalid.password ? `请输入${binding ? '旧密码' : '密码'}` : undefined}>
          <Input
            type="password"
            value={password}
            onChange={(event) => { setPassword(event.target.value); setInvalid((prev) => ({ ...prev, password: false })) }}
            placeholder="密码"
            autoComplete="current-password"
            error={invalid.password}
            aria-invalid={invalid.password || undefined}
            disabled={locked}
            className={heights}
          />
        </Field>
        <Button
          type="submit"
          variant={first ? 'secondary' : 'primary'}
          className={`w-full ${heights} font-semibold`}
          loading={pending}
          loadingMode="spinner"
          disabled={locked || (local.disableUntilFilled && (!username || !password))}
        >
          {pending ? (binding ? '绑定中…' : '登录中…') : (binding ? '绑定并登录' : '登录')}
        </Button>
      </form>
      {binding && local.onBack ? <Button variant="ghost" className="w-full" onClick={local.onBack}>返回其他登录方式</Button> : null}
    </>
  )
}

export function LoginMethods({ methods, onStart, local, children, busy = false }: LoginMethodsProps) {
  const [pending, setPending] = useState<ExternalMethod | null>(null)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    const release = () => setPending(null)
    window.addEventListener('pageshow', release)
    return () => window.removeEventListener('pageshow', release)
  }, [])
  const start = (method: ExternalMethod) => {
    setPending(method)
    const result = onStart(method)
    // 应用自己的 onStart 负责接住失败并显示出错；这里只管解锁，不再把失败往外抛
    if (result && typeof result.then === 'function') void result.then(() => setPending(null), () => setPending(null))
  }
  const external = methods.filter((method): method is ExternalMethod => method !== 'local')
  const hasLocal = methods.includes('local') && Boolean(local || children)
  const locked = pending !== null || submitting || busy
  const reportLocked = useContext(ReportMethodsLocked)
  useEffect(() => {
    reportLocked(locked)
    return () => reportLocked(false)
  }, [locked, reportLocked])
  const [first, ...rest] = external
  const renderButton = (method: ExternalMethod, primary: boolean) => (
    <Button
      key={method}
      variant={primary ? 'primary' : 'secondary'}
      className="w-full min-h-10! max-md:min-h-11! font-semibold"
      loading={pending === method}
      loadingMode="spinner"
      disabled={locked}
      onClick={() => start(method)}
    >
      {pending === method ? METHOD_PENDING_LABEL[method] : METHOD_LABEL[method]}
    </Button>
  )
  return (
    <fieldset disabled={locked} className="m-0 flex min-w-0 flex-col gap-[18px] border-0 p-0">
      {first ? renderButton(first, true) : null}
      {rest.length ? (
        <div className={`grid grid-cols-1 gap-2 ${rest.length > 1 ? 'md:grid-cols-2' : ''}`}>
          {rest.map((method) => renderButton(method, false))}
        </div>
      ) : null}
      {first && hasLocal ? (
        <div role="separator" className="flex items-center gap-3 text-xs text-[var(--color-faint)]">
          <span className="h-px flex-1 bg-[var(--color-line)]" />
          或用本系统账号登录
          <span className="h-px flex-1 bg-[var(--color-line)]" />
        </div>
      ) : null}
      {local ? <LocalLoginForm local={local} first={Boolean(first)} locked={locked} onBusy={setSubmitting} /> : children}
    </fieldset>
  )
}

/**
 * 登录失败的提示：统一成「对象 + 原因 + 下一步」，服务端原文一律保留。
 * 字符串是页面自己的输入校验（如空提交）：原文做标题，正文补下一步。
 * 401：标题带服务端原因，正文补下一步；其余（含 429 限流）原文放正文，服务端已写全原因和下一步，不再加话。
 * binding 为真时是绑定旧账号，服务端 401 原文已带绑定的下一步，只换标题。
 */
function LoginFailureNotice({ error, binding = false }: { error: unknown; binding?: boolean }) {
  const status = typeof error === 'object' && error !== null && 'status' in error ? Number((error as { status: unknown }).status) : 0
  const message = typeof error === 'string' && error ? error : error instanceof Error && error.message ? error.message : ''
  // 应用没传回任何失败原因：如实说没收到，不编原因，只给下一步
  if (!message) {
    return <Alert tone="danger" title={binding ? '绑定没有成功' : '登录没有成功'}>没有收到失败原因。请稍后重试；还是不行，请联系{binding ? '系统管理员确认' : '本系统管理员'}。</Alert>
  }
  if (typeof error === 'string' && error) {
    return <Alert tone="danger" title={error}>补齐后再点{binding ? '绑定并登录' : '登录'}。</Alert>
  }
  if (status === 401 && !binding) {
    return <Alert tone="danger" title={`登录没有成功：${message}`}>请检查大小写和输入法后重试。忘记密码，请联系本系统管理员重置。</Alert>
  }
  return <Alert tone="danger" title={binding ? '绑定没有成功' : '登录没有成功'}>{message}</Alert>
}
